import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileStack, Landmark, Loader2, Upload, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, downloadFile, formatErr } from "@/lib/api";
import { toast } from "sonner";

function ProofRow({ q, onSaved }) {
  const file = useRef(null);
  const [busy, setBusy] = useState(false);
  const send = async (f) => {
    const fd = new FormData(); fd.append("req_id", q.id); fd.append("file", f);
    setBusy(true);
    try { const { data } = await api.post("/roadmap/licensing/proof", fd); onSaved(data); toast.success("Proof saved and requirement marked met"); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 py-2.5 border-t border-slate-100 first:border-0" data-testid={`license-req-${q.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-800">{q.title}</div>
        <div className="text-[11px] text-slate-500">{[q.issuer, q.proof && `Proof: ${q.proof}`].filter(Boolean).join(" · ")}</div>
        <div className={`text-[11px] mt-0.5 ${q.met ? "text-amber-700" : "text-slate-400"}`}>{q.met ? "Marked met, proof still needed" : "Not met yet"}</div>
      </div>
      <input ref={file} type="file" className="hidden" onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) send(f); }} data-testid={`license-proof-input-${q.id}`} />
      <Button size="sm" variant="outline" className="h-8 shrink-0 self-start" disabled={busy} onClick={() => file.current.click()} data-testid={`license-proof-upload-${q.id}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}Upload proof</Button>
    </div>
  );
}

export function LicenseProofs({ license, onSaved }) {
  const [all, setAll] = useState(false);
  if (!license?.requirements?.length) return null;
  const todo = license.requirements.filter((q) => !q.proven);
  if (!todo.length) return <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1" data-testid="license-proofs-complete"><ShieldCheck className="h-3.5 w-3.5" />Every requirement has proof on file.</p>;
  const shown = all ? todo : todo.slice(0, 3);
  return (
    <div className="mt-3 border border-slate-200 rounded-md px-3 py-1 bg-slate-50/60" data-testid="license-proofs">
      <div className="text-xs font-semibold text-slate-600 py-2">{todo.length} requirement{todo.length > 1 ? "s" : ""} need proof</div>
      {shown.map((q) => <ProofRow key={q.id} q={q} onSaved={onSaved} />)}
      {todo.length > 3 && <button onClick={() => setAll(!all)} className="w-full text-left text-xs font-semibold text-emerald-700 py-2 hover:underline" data-testid="license-proofs-toggle">{all ? "Show fewer" : `Show all ${todo.length}`}</button>}
    </div>
  );
}

export function RerunBanner({ r, onStarted }) {
  const [city, setCity] = useState(r.license?.city || "");
  const [state, setState] = useState(r.license?.state || "");
  const [busy, setBusy] = useState(false);
  if (r.checking) return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 mb-6 flex items-center gap-3 text-sm text-slate-700" data-testid="roadmap-licensing-checking">
      <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />Building the license list for {r.industry}. This takes about a minute.</div>);
  if (!r.industry_mismatch) return null;
  const go = async () => {
    setBusy(true);
    try { await api.post("/roadmap/licensing/rerun", { industry: r.industry, city, state }); toast.success("New licensing check started"); onStarted(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-6" data-testid="roadmap-industry-mismatch">
      <div className="flex gap-3"><Landmark className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">Your licensing check is for <b>{r.license.industry}</b>, but your business is <b>{r.industry}</b>. Run a new check so the license list matches what funders expect.</p></div>
      <div className="flex flex-col sm:flex-row gap-2 mt-3 sm:pl-8">
        <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" className="h-9 bg-white sm:w-44" data-testid="roadmap-rerun-city" />
        <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="State" className="h-9 bg-white sm:w-44" data-testid="roadmap-rerun-state" />
        <Button size="sm" className="h-9 bg-slate-900 hover:bg-slate-800" disabled={busy || state.trim().length < 2} onClick={go} data-testid="roadmap-industry-mismatch-button">{busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Run a new check for {r.industry}</Button>
      </div>
    </div>
  );
}

export function PacketButtons({ r }) {
  const [busy, setBusy] = useState(null);
  const kinds = r.path === "both" ? ["loan", "grant"] : r.path ? [r.path] : [];
  if (!kinds.length) return null;
  const get = async (k) => {
    setBusy(k);
    try { await downloadFile(`/roadmap/packet/${k}.pdf`, `${(r.company || "Business").replace(/\W+/g, "_")}_${k === "loan" ? "Lender" : "Grant"}_Packet.pdf`); }
    catch (e) { toast.error("Couldn't build the packet. Please try again."); } finally { setBusy(null); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="roadmap-packets">
      {kinds.map((k) => (
        <Button key={k} size="sm" variant="outline" className="h-9" disabled={!!busy} onClick={() => get(k)} data-testid={`roadmap-packet-${k}`}>
          {busy === k ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileStack className="h-4 w-4 mr-2" />}Download {k === "loan" ? "lender" : "grant"} packet</Button>))}
      <span className="text-xs text-slate-500">Cover page, contents and every finished PDF in one file.</span>
      <Link to="/dashboard/documents" className="text-xs text-emerald-700 hover:underline">Manage files</Link>
    </div>
  );
}
